import {
    ref,
    set,
    get,
    update, 
    remove, 
    push, 
    Database 
  } from "firebase/database";
  
/**
 * Helper methods for interacting with Firebase Realtime Database.
 */
export interface FirebaseRealtimeHelper {
  /** Sets data at the given path. */
  setData: (path: string, data: any) => Promise<void>;
  /** Reads data from the given path. */
  getData: (path: string) => Promise<any>;
  /** Updates data at the given path. */
  updateData: (path: string, data: any) => Promise<void>;
  /** Deletes the record at the given path. */
  deleteData: (path: string) => Promise<void>;
  /** Pushes new data at the path and returns the generated key. */
  pushData: (path: string, data: any) => Promise<string | null>;
}
  
/**
 * Creates realtime database helper functions bound to the given database instance.
 *
 * @param database - Firebase database instance or `null` if not initialized.
 * @returns Object with common realtime operations.
 */
const useFirebaseRealtimeHelper = (database: Database | null): FirebaseRealtimeHelper => {
    const setData = async (path: string, data: any) => {
      try {
        if (database) {
          const dataRef = ref(database, path);
          await set(dataRef, data);
        } else {
          throw "Firebase Realtime Database no configurado correctamente";
        }
      } catch (error) {
        throw error;
      }
    };
  
    const getData = async (path: string) => {
      try {
        if (database) {
          const dataRef = ref(database, path);
          const snapshot = await get(dataRef);
          if (snapshot.exists()) {
            return snapshot.val();
          } else {
            return null;
          }
        }
        throw "Firebase Realtime Database no configurado correctamente";
      } catch (error) {
        throw error;
      }
    };
  
    const updateData = async (path: string, data: any) => {
      try {
        if (database) {
          const dataRef = ref(database, path);
          await update(dataRef, data);
        } else {
          throw "Firebase Realtime Database no configurado correctamente";
        }
      } catch (error) {
        throw error;
      }
    };
  
    const deleteData = async (path: string) => {
      try {
        if (database) {
          const dataRef = ref(database, path);
          await remove(dataRef);
        } else {
          throw "Firebase Realtime Database no configurado correctamente";
        }
      } catch (error) {
        throw error;
      }
    };
  
    const pushData = async (path: string, data: any) => {
      try {
        if (database) {
          const dataRef = ref(database, path);
          const newRef = push(dataRef);
          await set(newRef, data);
          return newRef.key;
        }
        throw "Firebase Realtime Database no configurado correctamente";
      } catch (error) {
        throw error;
      }
    };
  
    return {
      setData,
      getData,
      updateData,
      deleteData,
      pushData
    };
  };
  
  export default useFirebaseRealtimeHelper;
  